import { Autocomplete, Checkbox, TextField } from "@mui/material";
import { inlineChoiceControlSx } from "../../../common/dashboard/formStyles";
import React, { useState } from "react";
import { formatStaffPhoneInput } from "../../../../utils/memberContactFormatting";
import {
  invoiceAttendeeMenuSx,
  invoiceAttendeeNoMatchesSx,
  invoiceFieldSx,
} from "../../Invoicing/InvoiceDesignSystem";
import InvoiceSearchableSelect from "../../Invoicing/InvoiceSearchableSelect";
import type { EventAttendeeChoiceFields } from "../eventBackendMappers";
import {
  eventSpeakerQuestionMaxLength,
  parseEventCustomFieldChoiceValue,
  serializeEventCustomFieldChoiceValue,
} from "./eventSalesAttendeeUpdates";
import {
  filterEventAttendeeComboOptions,
  type EventAttendeeSuggestion,
} from "./eventAttendeeSuggestions";
import {
  eventAttendeeStaffNotesHelper,
  eventAttendeeStaffNotesMaxLength,
  getEventAttendeeDietaryCopy,
} from "./eventInvoiceCreateState";

export type EventAttendeeDetailValues = Readonly<{
  companyName?: string | null;
  customFieldAnswers: Readonly<Record<string, string>>;
  email: string;
  name: string;
  phone: string;
  staffNotes?: string;
}>;

export type EventAttendeeDetailPatch = Partial<{
  companyName: string;
  customFieldAnswers: Record<string, string>;
  email: string;
  name: string;
  phone: string;
  staffNotes: string;
}>;

const fieldLabelClassName = "text-[14px] font-bold leading-[normal] text-[#0A0A0A]";
const questionTitleClassName = "text-[15px] font-bold leading-[normal] text-[#0A0A0A]";
const questionHelperClassName = "m-0 text-[14px] leading-[1.6] text-[#0A0A0A]";

const RequiredStar = () => <span className="text-[#E24C4C]">*</span>;

/**
 * One attendee's details, the same on Create Ticket Invoice, Create
 * Sponsorship Invoice and Update Attendee Details (CMP Dashboard (21)
 * :5579-5638 and UpdateAttendeeNamesModal.jsx:172-238; IF11 EV7): numbered
 * circle, name and company combos, email, phone, the event questions and
 * Additional Details.
 */
const EventAttendeeDetailFields = ({
  ariaName,
  choiceFields,
  companyOptions = [],
  disabled = false,
  emailError,
  fieldId,
  fieldSx = {},
  nameError,
  number,
  onChange,
  onClearError,
  onPickSuggestion,
  primary,
  suggestions = [],
  values,
}: Readonly<{
  /** "Attendee 3", used in the field aria labels. */
  ariaName: string;
  choiceFields: EventAttendeeChoiceFields;
  companyOptions?: readonly string[];
  disabled?: boolean;
  emailError?: string;
  fieldId: (suffix: "company" | "dietary" | "email" | "name" | "phone" | "speaker" | "staff-notes") => string;
  fieldSx?: object;
  nameError?: string;
  /** The number in the gold circle. */
  number: number;
  onChange: (patch: EventAttendeeDetailPatch) => void;
  onClearError: (field: "email" | "name") => void;
  onPickSuggestion: (suggestion: EventAttendeeSuggestion) => void;
  /** Attendee 1 of the record: name and email carry the star. */
  primary: boolean;
  suggestions?: readonly EventAttendeeSuggestion[];
  values: EventAttendeeDetailValues;
}>): React.ReactElement => {
  // Design AttendeeNameCombo: the full list on focus; typing filters by
  // names that start with the text.
  const [typedField, setTypedField] = useState<"company" | "name" | null>(null);
  const { accommodations, dietary, dietaryIsMealChoice, speaker } = choiceFields;
  // CMP Dashboard (21) Q_DEFAULT_TEXT (:11484-11491): titles only, no helper
  // text under the accommodation and dietary questions (IF11 Q13, Q14).
  const dietaryCopy = getEventAttendeeDietaryCopy(dietaryIsMealChoice);
  const answers = values.customFieldAnswers;
  const updateAnswer = ({ fieldId: answerFieldId, value }: { fieldId: string; value: string }) =>
    onChange({ customFieldAnswers: { ...answers, [answerFieldId]: value } });
  const dietaryValue = dietary ? answers[dietary.id] || "" : "";
  const dietaryOptions = dietary
    ? [
        ...dietary.choices,
        ...(dietaryValue && !dietary.choices.includes(dietaryValue)
          ? [dietaryValue]
          : []),
      ]
    : [];
  const accommodationValues = parseEventCustomFieldChoiceValue({
    choices: accommodations?.choices || [],
    value: accommodations ? answers[accommodations.id] : "",
  });
  const selectedAccommodations = [
    ...accommodationValues.selected,
    ...accommodationValues.extras,
  ];
  const speakerAnswer = speaker ? answers[speaker.id] || "" : "";

  return (
    <div className="flex items-start gap-4">
      <span
        aria-hidden="true"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#FBF1DC] text-[14px] font-bold text-[#0A0A0A]"
      >
        {number}
      </span>
      <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-6 gap-y-[22px] md:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-[10px]">
          <label className={fieldLabelClassName} htmlFor={fieldId("name")}>
            Attendee Name {primary ? <RequiredStar /> : null}
          </label>
          <Autocomplete<EventAttendeeSuggestion, false, true, true>
            // The Autocomplete id becomes the input id, so the label and the
            // focus-on-error reach the input.
            id={fieldId("name")}
            disabled={disabled}
            disableClearable
            forcePopupIcon
            freeSolo
            openOnFocus
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.name
            }
            filterOptions={(options, state) =>
              filterEventAttendeeComboOptions({
                getLabel: (option) => option.name,
                options,
                text: typedField === "name" ? state.inputValue : "",
              })
            }
            groupBy={() => "PREVIOUS ATTENDEES & MEMBER CONTACTS"}
            inputValue={values.name}
            onClose={() => setTypedField(null)}
            isOptionEqualToValue={(option, value) =>
              typeof value !== "string" && option.id === value.id
            }
            onChange={(_event, option) => {
              if (typeof option === "string") {
                onChange({ name: option });
                onClearError("name");
                return;
              }

              onPickSuggestion(option);
              onClearError("name");
              onClearError("email");
            }}
            onInputChange={(_event, value, reason) => {
              if (reason === "input" || reason === "clear") {
                setTypedField("name");
                onChange({ name: value });
                onClearError("name");
              }
            }}
            options={suggestions}
            renderGroup={(params) => (
              <li key={params.key}>
                <div className="px-3 py-[7px] text-[11px] font-bold tracking-[0.05em] text-[#9E9E9E]">
                  {params.group}
                </div>
                <ul className="m-0 p-0">{params.children}</ul>
              </li>
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <div className="min-w-0 py-0.5">
                  <p className="flex items-center gap-2 truncate text-sm text-[#0A0A0A]">
                    {option.name}
                    {option.role ? (
                      <span className="rounded-full border border-[#D6D6D6] px-[7px] py-px text-[11px] font-semibold text-[#727272]">
                        {option.role}
                      </span>
                    ) : null}
                  </p>
                  {option.email || option.phone ? (
                    <p className="truncate text-xs text-[#9E9E9E]">
                      {[option.email, option.phone]
                        .filter(Boolean)
                        .join(" • ")}
                    </p>
                  ) : null}
                </div>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                error={Boolean(nameError)}
                fullWidth
                helperText={nameError}
                inputProps={{
                  ...params.inputProps,
                  "aria-label": `${ariaName} name`,
                }}
                placeholder="Enter attendee name"
                size="small"
                sx={[invoiceFieldSx, fieldSx]}
              />
            )}
            componentsProps={{
              paper: {
                sx: {
                  border: "1px solid #ECECEC",
                  borderRadius: "10px",
                  boxShadow: "0 8px 24px rgba(0,0,0,.14)",
                  mt: 0.75,
                },
              },
            }}
            ListboxProps={{
              sx: {
                py: 0.75,
                "& .MuiAutocomplete-option": {
                  alignItems: "flex-start",
                  minHeight: 52,
                  px: 1.5,
                  py: 1,
                  "&[aria-selected='true']": {
                    backgroundColor: "rgba(225,167,49,.10)",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "rgba(225,167,49,.10)",
                  },
                },
              },
            }}
            value={values.name}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-[10px]">
          <label className={fieldLabelClassName} htmlFor={fieldId("company")}>
            Attendee Company
          </label>
          {/* Design (21) :5590: company combo, heading "MEMBERS". */}
          <Autocomplete<string, false, true, true>
            id={fieldId("company")}
            disabled={disabled}
            disableClearable
            filterOptions={(options, state) =>
              filterEventAttendeeComboOptions({
                getLabel: (option) => option,
                options,
                text: typedField === "company" ? state.inputValue : "",
              })
            }
            forcePopupIcon
            freeSolo
            groupBy={() => "MEMBERS"}
            inputValue={values.companyName || ""}
            onChange={(_event, option) =>
              onChange({ companyName: (option || "").slice(0, 200) })
            }
            onClose={() => setTypedField(null)}
            onInputChange={(_event, value, reason) => {
              if (reason === "input" || reason === "clear") {
                setTypedField("company");
                onChange({ companyName: value.slice(0, 200) });
              }
            }}
            openOnFocus
            options={[...companyOptions]}
            renderGroup={(params) => (
              <li key={params.key}>
                <div className="px-3 py-[7px] text-[11px] font-bold tracking-[0.05em] text-[#9E9E9E]">
                  {params.group}
                </div>
                <ul className="m-0 p-0">{params.children}</ul>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                inputProps={{
                  ...params.inputProps,
                  "aria-label": `${ariaName} company name`,
                  maxLength: 200,
                }}
                placeholder="Select member or enter company name"
                size="small"
                sx={[invoiceFieldSx, fieldSx]}
              />
            )}
            componentsProps={{
              paper: {
                sx: {
                  border: "1px solid #ECECEC",
                  borderRadius: "10px",
                  boxShadow: "0 8px 24px rgba(0,0,0,.14)",
                  mt: 0.75,
                },
              },
            }}
            ListboxProps={{
              sx: {
                py: 0.75,
                "& .MuiAutocomplete-option": {
                  px: 1.5,
                  py: 1,
                  "&.Mui-focused": { backgroundColor: "rgba(225,167,49,.10)" },
                },
              },
            }}
            value={values.companyName || ""}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-[10px]">
          <label className={fieldLabelClassName} htmlFor={fieldId("email")}>
            Attendee Email {primary ? <RequiredStar /> : null}
          </label>
          <TextField
            disabled={disabled}
            fullWidth
            id={fieldId("email")}
            inputProps={{
              "aria-label": `${ariaName} email`,
              "aria-required": primary,
            }}
            error={Boolean(emailError)}
            helperText={emailError}
            onChange={({ target }) => {
              onChange({ email: target.value });
              onClearError("email");
            }}
            placeholder="Enter attendee email"
            size="small"
            sx={[invoiceFieldSx, fieldSx]}
            type="email"
            value={values.email}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-[10px]">
          <label className={fieldLabelClassName} htmlFor={fieldId("phone")}>
            Attendee Phone Number
          </label>
          <TextField
            disabled={disabled}
            fullWidth
            id={fieldId("phone")}
            inputProps={{ "aria-label": `${ariaName} phone` }}
            onChange={({ target }) =>
              onChange({ phone: formatStaffPhoneInput(target.value) })
            }
            placeholder="Enter phone number"
            size="small"
            sx={[invoiceFieldSx, fieldSx]}
            type="tel"
            value={values.phone}
          />
        </div>
        {accommodations || dietary ? (
          // CLAUDE.md "Ticket attendee questions": a fixed 2-column
          // grid; one enabled question keeps its single column.
          <div
            className="grid min-w-0 grid-cols-1 gap-x-6 gap-y-4 md:col-span-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:[grid-template-rows:auto_auto_auto] md:gap-y-0"
            data-attendee-choice-row
          >
            {accommodations ? (
              <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-y-[6px] md:row-span-3 md:[grid-template-rows:subgrid]">
                <p className={`m-0 ${questionTitleClassName}`}>
                  Do you have any special accommodations?
                </p>
                <div className="flex flex-col gap-[10px] pl-1 pt-[6px]">
                  {accommodations.choices.map((choice) => (
                    <label
                      className="flex items-center gap-3 text-[14px] text-[#0A0A0A]"
                      key={choice}
                    >
                      <Checkbox
                        inputProps={{ "aria-label": `${ariaName} ${choice}` }}
                        sx={inlineChoiceControlSx}
                        checked={selectedAccommodations.includes(choice)}
                        disabled={disabled}
                        onChange={({ target }) =>
                          updateAnswer({
                            fieldId: accommodations.id,
                            value: serializeEventCustomFieldChoiceValue({
                              choices: accommodations.choices,
                              selectedValues: target.checked
                                ? [...selectedAccommodations, choice]
                                : selectedAccommodations.filter(
                                    (value) => value !== choice,
                                  ),
                            }),
                          })
                        }
                      />
                      <span>{choice}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
            {dietary ? (
              <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-y-[6px] md:row-span-3 md:[grid-template-rows:subgrid]">
                <p className={`m-0 ${questionTitleClassName}`}>
                  {dietaryCopy.title}
                </p>
                <div className="max-w-[320px] pt-[6px]">
                  <InvoiceSearchableSelect
                    ariaLabel={`${ariaName} ${dietaryIsMealChoice ? "meal option" : "dietary option"}`}
                    disabled={disabled}
                    getOptionKey={(option) => option}
                    getOptionLabel={(option) => option}
                    id={fieldId("dietary")}
                    noOptionsSx={invoiceAttendeeNoMatchesSx}
                    menuSx={invoiceAttendeeMenuSx}
                    noOptionsText="No matches found"
                    outlinedArrow
                    onChange={(value) =>
                      updateAnswer({
                        fieldId: dietary.id,
                        value: value || "",
                      })
                    }
                    onClear={() =>
                      updateAnswer({
                        fieldId: dietary.id,
                        value: "",
                      })
                    }
                    optionRowHeight={40}
                    options={dietaryOptions}
                    placeholder={dietaryCopy.placeholder}
                    searchPlaceholder={dietaryCopy.search}
                    value={dietaryValue || null}
                  />
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        {speaker ? (
          <div className="flex min-w-0 flex-col gap-[6px] md:col-span-2">
            <label className={questionTitleClassName} htmlFor={fieldId("speaker")}>
              Submit Speaker Question
            </label>
            <p className={questionHelperClassName}>
              Submit a question you would like asked of the speaker or panellists. We will do our best to ask it on your behalf, but cannot guarantee all questions will be included due to time availability.
            </p>
            <TextField
              disabled={disabled}
              fullWidth
              id={fieldId("speaker")}
              inputProps={{
                "aria-label": `${ariaName} speaker question`,
                maxLength: eventSpeakerQuestionMaxLength,
              }}
              minRows={3}
              multiline
              onChange={({ target }) =>
                updateAnswer({
                  fieldId: speaker.id,
                  value: target.value.slice(0, eventSpeakerQuestionMaxLength),
                })
              }
              placeholder="Enter Question"
              size="small"
              sx={[invoiceFieldSx, fieldSx, { mt: "4px" }]}
              value={speakerAnswer}
            />
            <span className="text-[11px] text-[#727272]">
              {speakerAnswer.length}/{eventSpeakerQuestionMaxLength} characters
            </span>
          </div>
        ) : null}
        {/* CMP Dashboard (21) :5634-5637 Additional Details (IF11 EV9):
            staff notes, 500 characters, no counter. */}
        <div className="flex min-w-0 flex-col gap-[6px] md:col-span-2">
          <label className={questionTitleClassName} htmlFor={fieldId("staff-notes")}>
            Additional Details
          </label>
          <p className="m-0 text-[13px] leading-[1.5] text-[#727272]">
            {eventAttendeeStaffNotesHelper}
          </p>
          <TextField
            disabled={disabled}
            fullWidth
            id={fieldId("staff-notes")}
            inputProps={{
              "aria-label": `${ariaName} additional details`,
              maxLength: eventAttendeeStaffNotesMaxLength,
            }}
            minRows={2}
            multiline
            onChange={({ target }) =>
              onChange({
                staffNotes: target.value.slice(0, eventAttendeeStaffNotesMaxLength),
              })
            }
            placeholder="Enter Staff Notes"
            size="small"
            sx={[invoiceFieldSx, fieldSx]}
            value={values.staffNotes || ""}
          />
        </div>
      </div>
    </div>
  );
};

export default EventAttendeeDetailFields;
